import { select, observe } from './_util.js';

class Lines {
	constructor( node, opts = {} ) {
		this.node = node;
		this.opts = {
			delay: 1,
			duration: null,
			interval: 125,
			threshold: 50,
			...opts
		}

		this.node.classList.add( 'animated-lines' );

		this.words     = this.node.textContent.split( ' ' );
		this.wordQueue = [];

		this.placeholder = document.createElement( 'span' );
		this.placeholder.classList.add( 'animated-lines__placeholder' );

		Array.from( this.node.childNodes ).forEach( childNode => this.placeholder.appendChild( childNode ) );
		this.node.appendChild( this.placeholder );

		const computedStyle = getComputedStyle( this.node );

		this.lines = document.createElement('span');
		this.lines.classList.add( 'animated-lines__lines' );
		this.lines.setAttribute( 'aria-hidden', true );

		[
			'paddingTop',
			'paddingBottom',
			'borderTopWidth',
			'borderBottomWidth',
		].forEach( prop => {
			if ( parseInt( computedStyle[ prop ], 10 ) > 0 ) {
				this.lines.style[ prop ] = computedStyle[ prop ];
			}
		} );

		this.node.appendChild( this.lines );

		observe( this.node, { threshold: this.opts.threshold } ).then( () => {
			this.prepare().then( () => {
				setTimeout( () => this.animate(), this.opts.delay );
			} );
		} );
	}

	lineItems() {
		return this.node.querySelectorAll('.animated-lines__line');
	}

	prepare() {
		const wordQueue = [ ...this.words ];

		// empty the lines node
		while ( this.lines.firstChild ) {
			this.lines.removeChild( this.lines.firstChild );
		}

		const processWords = () => {
			return new Promise(( resolve, reject ) => {
				const word = wordQueue.shift();

				const wordNode = document.createElement( 'span' );
				wordNode.classList.add( 'animated-lines__word' );
				wordNode.appendChild( document.createTextNode( word ) );

				// add a slight delay to the measurements so they're more accurate
				setTimeout( () => {
					if ( ! this.lines.lastElementChild ) {
						this.appendLine();
					}

					this.lines.lastElementChild.appendChild( document.createTextNode( ' ' ) );
					this.lines.lastElementChild.appendChild( wordNode );

					// append each word to a 'line' and if the offsetTop doesn't match the previous word, move it to the next 'line'
					if ( wordNode.offsetParent && wordNode.offsetLeft + wordNode.offsetWidth > wordNode.offsetParent.offsetWidth ) {
						this.appendLine();
						this.lines.lastElementChild.appendChild( wordNode );
					}

					if ( wordQueue.length ) {
						resolve( processWords() );
					} else {
						resolve();
					}
				}, 1);
			});
		}

		return processWords();
	}

	appendLine() {
		const newLine = document.createElement( 'span' );
		newLine.classList.add( 'animated-lines__line' );
		this.lines.appendChild( newLine );
	}

	reset() {
		const linesNode = this.node.querySelector( '.animated-lines__lines' );

		if ( linesNode ) {
			linesNode.parentNode.removeChild( linesNode );
		}

		if ( this.placeholder && this.placeholder.parentNode ) {
			this.placeholder.childNodes.forEach( childNode => this.node.appendChild( childNode ) );
			this.placeholder.parentNode.removeChild( this.placeholder );
		}

		this.node.classList.remove( 'animated-lines' );

		this.node.dataset.animated = true;

		return this;
	}

	animate() {
		return new Promise( ( resolve ) => {
			this.lineItems().forEach( ( line, lineIndex, lines ) => {
				setTimeout(
					() => {
						line.classList.add( 'animated-lines__line--active' );

						if ( lineIndex === lines.length - 1 ) {
							let duration = this.opts.duration;

							if ( ! duration ) {
								duration = parseFloat( getComputedStyle( line ).animationDuration ) * 1000;
							}

							setTimeout( () => resolve( this.reset() ), duration * 2 );
						}
					},
					this.opts.interval * ( lineIndex + 1 )
				);
			} );
		});
	}
}

/**
 * Animate each line of a textNode in individually
 *
 * @param {String|Array|NodeList|HTMLElement} selector
 * @param {Object} opts
 * @returns {Array}
 */
export function lines( selector, opts = {} ) {
	return select( selector ).map( node => new Lines( node, opts ) );
}
